import {
  generateDesignSuggestion,
  type DesignSuggestionPosition,
} from '@/api/openai';
import * as productsApi from '@/api/products';
import { productKeys } from '@/hooks/useProducts';
import { getIframeLanguage } from '@/lib/i18n';
import { queryClient } from '@/lib/queryClient';
import {
  getEffectiveCropSrc,
  useCanvasStateManager,
} from '@/stores/canvasStateManager';
import { useProductSelectionStore } from '@/stores/productSelectionStore';
import { usePublishStore } from '@/stores/publishStore';
import { extractCanvasTexts } from '@/utils/canvasTextExtractor';
import { logger } from '@/utils/logger';

/** Mirrors the caps enforced by the API DTO. */
const MAX_POSITIONS = 10;
const MAX_CATEGORY_NAMES = 20;

function isUsableImageUrl(url: string): boolean {
  return (
    url.startsWith('http://') ||
    url.startsWith('https://') ||
    url.startsWith('data:image/')
  );
}

/**
 * One entry per position that carries objects: the cropped design image
 * (never the mockup preview) plus the plain text of its text objects.
 * Reads the persisted position state because the canvas is gone by the
 * time the Publish step renders.
 */
export function collectSuggestionPositions(): DesignSuggestionPosition[] {
  const { masterPositions, getPositionsWithDesigns } =
    useCanvasStateManager.getState();

  return getPositionsWithDesigns()
    .slice(0, MAX_POSITIONS)
    .map((positionKey) => {
      const position = masterPositions[positionKey];
      const crop = position ? getEffectiveCropSrc(position) : null;
      return {
        positionKey,
        ...(crop && isUsableImageUrl(crop) ? { imageUrl: crop } : {}),
        texts: extractCanvasTexts(position?.json ?? null),
      };
    });
}

/**
 * Category names of the active product selections. The API resolves them
 * for the request's Shop Language (Accept-Language), so the names arrive
 * localised. Categories are only a hint for the model, so a failed product
 * lookup degrades to an empty list instead of failing the suggestion.
 */
export async function collectCategoryNames(): Promise<string[]> {
  const activeIds = Object.entries(
    useProductSelectionStore.getState().selections
  )
    .filter(([, selection]) => selection.isActive)
    .map(([productId]) => productId);
  if (activeIds.length === 0) return [];

  try {
    const products = await queryClient.fetchQuery({
      queryKey: productKeys.list(undefined),
      queryFn: () => productsApi.getProducts(),
    });
    const names = new Set<string>();
    for (const product of products) {
      if (!activeIds.includes(product.id) || !product.category) continue;
      // The API already resolved the name for the request language.
      const name = product.category.name;
      const trimmed = name?.trim();
      if (trimmed) names.add(trimmed);
    }
    return [...names].slice(0, MAX_CATEGORY_NAMES);
  } catch (error) {
    logger.warn(
      'AiProductDescription',
      'Product categories unavailable, continuing without them',
      error
    );
    return [];
  }
}

export function hasSuggestionContext(
  positions: DesignSuggestionPosition[]
): boolean {
  return positions.some(
    (position) => Boolean(position.imageUrl) || position.texts.length > 0
  );
}

/**
 * Run the AI suggestion for title, description and tags. Called once per
 * design when the seller reaches the products step and again from the
 * "Neu vorschlagen" button in the Publish step. The result replaces all
 * three fields unless the store moved on to another design meanwhile.
 */
export async function startAiProductDescriptionGeneration(): Promise<void> {
  if (usePublishStore.getState().aiGenerationStatus === 'loading') {
    return;
  }

  const requestId = usePublishStore.getState().beginAiGeneration();

  try {
    const language = getIframeLanguage();
    const positions = collectSuggestionPositions();
    if (!hasSuggestionContext(positions)) {
      throw new Error('No design content available for the AI suggestion');
    }
    const categoryNames = await collectCategoryNames();

    const result = await generateDesignSuggestion({
      language,
      positions,
      categoryNames,
    });
    usePublishStore.getState().applyAiGenerationResult(requestId, result);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'AI suggestion failed';
    logger.error('AiProductDescription', message, error);
    usePublishStore.getState().failAiGeneration(requestId, message);
  }
}
